import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { notasVentaApi } from '../../api/notasVenta'
import EstadoBadge, { COLORES, LABELS } from '../../components/EstadoBadge'
import useDebounce from '../../hooks/useDebounce'
import { useAuth } from '../../context/AuthContext'
import { fmtFecha, fmtHoraDe } from '../../utils/fecha'
import { colors, font, text } from '../../design/tokens'
import { Card, Buscador, ChipsScroll, Cargando, ErrorMsg, Vacio, BotonCargarMas, PagoDot, fmtMoney, Titulo } from './comunes'

// Listado móvil de notas de venta — mismo endpoint que /notas-venta.
// Chips con la MISMA paleta que el badge (exportada por EstadoBadge).
const ESTADO_OPCIONES = ['borrador', 'procesado', 'frenado']
  .map((v) => ({ value: v, label: LABELS[v], ...COLORES[v] }))

export default function MovilVentas() {
  const navigate = useNavigate()
  const { rolOperativoVentas } = useAuth()
  const [q, setQ] = useState('')
  const qBuscar = useDebounce(q, 350)
  const [estados, setEstados] = useState([])
  const [nvs, setNvs] = useState([])
  const [pag, setPag] = useState({ current_page: 0, last_page: 1, total: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Evita que una respuesta vieja (búsqueda anterior, lenta) pise a la nueva
  const reqId = useRef(0)

  const cargar = useCallback(async (page) => {
    const id = ++reqId.current
    if (page === 1) setLoading(true)
    try {
      const params = { page }
      if (qBuscar) params.q = qBuscar
      if (estados.length > 0) params.estados = estados.join(',')
      const { data } = await notasVentaApi.listar(params)
      if (id !== reqId.current) return
      setNvs((prev) => (page === 1 ? data.data : [...prev, ...data.data]))
      setPag({ current_page: data.current_page, last_page: data.last_page, total: data.total })
      setError(null)
    } catch {
      if (id === reqId.current) setError('No se pudieron cargar las notas de venta.')
    } finally {
      if (id === reqId.current) setLoading(false)
    }
  }, [qBuscar, estados])

  useEffect(() => { cargar(1) }, [cargar])

  const toggleEstado = (v) =>
    setEstados((prev) => (prev.includes(v) ? prev.filter((e) => e !== v) : [...prev, v]))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}><Titulo>Ventas</Titulo></div>
        {rolOperativoVentas() && (
          <button onClick={() => navigate('/m/ventas/nueva')} style={btnNueva}>+ Nueva</button>
        )}
      </div>
      <Buscador value={q} onChange={setQ} placeholder="Buscar por número o cliente…" />
      <ChipsScroll options={ESTADO_OPCIONES} selected={estados} onToggle={toggleEstado} />

      {loading ? <Cargando /> : error ? <ErrorMsg>{error}</ErrorMsg> : nvs.length === 0 ? (
        <Vacio>No hay notas de venta que coincidan.</Vacio>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          {nvs.map((nv) => (
            <Card key={nv.id} onClick={() => navigate(`/m/ventas/${nv.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={mono}>{nv.numero_lgr}</span>
                <span style={{ flex: 1 }} />
                <PagoDot estado={nv.pago_estado} />
                <EstadoBadge estado={nv.estado} />
              </div>
              <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14.5, color: colors.text, margin: '5px 0 3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nv.cliente?.nombre || '—'}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: text.sec, color: colors.textMeta, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {fmtFecha(nv.fecha)}
                  {/* Hora de creación del pedido (created_at en hora ART), como en /notas-venta — Martin, 28 sep 2026 */}
                  {fmtHoraDe(nv.created_at) && <span style={{ marginLeft: 5, color: colors.textPlh }}>{fmtHoraDe(nv.created_at)}</span>}
                </span>
                {nv.items_count != null && (
                  <span style={{ fontSize: text.sec, color: colors.textMeta, whiteSpace: 'nowrap' }}>· {nv.items_count} ítems</span>
                )}
                {nv.numero_presea && nv.numero_presea !== '0' && (
                  <span style={{ fontSize: text.sec, color: colors.textMeta, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>· FAC {nv.numero_presea}</span>
                )}
                <span style={{ flex: 1 }} />
                <span style={{ ...mono, fontWeight: 700, fontSize: 13.5 }}>{fmtMoney(nv.monto_a_cobrar ?? nv.total)}</span>
              </div>
            </Card>
          ))}
          {pag.current_page < pag.last_page && (
            <BotonCargarMas onClick={() => cargar(pag.current_page + 1)} cargando={false} />
          )}
        </div>
      )}
    </div>
  )
}

const mono = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: colors.text }
const btnNueva = {
  border: 'none', background: colors.primary, color: '#fff', borderRadius: 10,
  padding: '11px 16px', fontSize: 13.5, fontWeight: 700, fontFamily: font.sans,
  cursor: 'pointer', flexShrink: 0,
}
